<template lang="pug">
    .card.right-side-card.payment-card
        h5.card-header Checkout
        .card-body
            re-input(label="" placeholder="First Name*" v-model.trim="$v.fields.first_name.$model" :validations="validations.first_name" :v="$v.fields.first_name" @input="$v.fields.first_name.$reset")
            re-input(label="" placeholder="Last Name*" v-model.trim="$v.fields.last_name.$model" :validations="validations.last_name" :v="$v.fields.last_name" @input="$v.fields.last_name.$reset")
            re-input(label="" placeholder="Email Address*" v-model.trim="$v.fields.email.$model" :validations="validations.email" :v="$v.fields.email" @input="$v.fields.email.$reset")
            re-input(label="" placeholder="Phone*" v-model.trim="$v.fields.phone.$model" :validations="validations.phone" :v="$v.fields.phone" @input="$v.fields.phone.$reset")

            #stripe-form-container(v-once)
                .form-group
                    label Card Number
                    #stripe-card-number.form-control
                .form-group
                    img(:src="`${baseUri}/images/client/lairdnd/payment-methods.svg`" alt="VISA, MASTERCARD")
                .row.payment-expiration-cvv
                    .col-6
                        .form-group
                            label Month / Year
                            #stripe-expiration-date.form-control
                    .col-6
                        .form-group.text-right
                            label Card Security Code
                            #stripe-cvv.third.form-control

            hr
            .two-cols.booking-subtotal
                span Subtotal
                span {{ subtotal | currency }}
            .two-cols.booking-subtotal.mt-3(v-if="mileageFee > 0")
                span Mileage Fee:
                span {{ mileageFee | currency }}
            hr
            re-toast-alert.mb-4(v-if="paymentError" type="danger") {{ paymentError }}
            re-button.btn-danger.btn-lg(@click="submit" :loading="paying" :disabled="$v.$dirty && $v.$invalid") Submit
            .service-fee-hint The total cost above doesn't include ${{ serviceFee }} service fee.

</template>

<script>
import ComponentMixin from 'utils/vue/mixins/components/complex'
import FormMixin from 'utils/vue/mixins/form'
import { parseValidations, setValidationRule, parseValidationSetup } from 'utils/vue/lib/validations'
import { getFirstErrorMessage } from 'utils/vue/services/main'
import { mapGetters } from 'vuex'

const STRIPE_PUBLISHABLE_KEY = window.addiesaas.stripe.publishable_key

const VALIDATIONS = {
    first_name: setValidationRule('fields.first_name', {
        required: { error: 'First name is required' }
    }),
    last_name: setValidationRule('fields.last_name', {
        required: { error: 'Last name is required' }
    }),
    email: setValidationRule('fields.email', {
        required: { error: 'Email is required' }
    }),
    phone: setValidationRule('fields.phone', {
        required: { error: 'Phone is required' }
    }),
}

export default {
    name: 'payment',

    mixins: [
        ComponentMixin,
        FormMixin,
    ],

    props: {
        profile: {
            type: Object,
            required: true,
        },
        booking: {
            type: Object,
            required: true,
        },
    },

    data() { return {
        fields: {
            first_name: '',
            last_name: '',
            email: '',
            phone: '',
        },
        stripe: null,
        elements: null,
        paymentError: null,
        paying: false,
    }},

    validations() {
        const fields = parseValidations(VALIDATIONS)
        return { fields }
    },

    computed: {
        ...mapGetters(['authUser']),

        product() {
            return this.profile.product.data
        },
        item() {
            return this.booking.payload.items[0]
        },
        addonsPrice() {
            return Object.entries(this.item.options.upgrades).reduce((a, c) => {
                return a + this.product.upgrades.find(u => u.name == c[0]).price * c[1]
            }, 0)
        },
        subtotal() {
            return this.addonsPrice + this.product.rates[0].price * this.item.options.slots
        },
        mileageFee() {
            return this.booking.response.price - this.subtotal
        },
        serviceFee() {
            return window.addiesaas.lairdnd.gamemaster.service_fee
        },
    },

    async created() {
        this.fields.first_name = this.authUser.first_name
        this.fields.last_name = this.authUser.last_name
        this.fields.email = this.authUser.email
        this.fields.phone = this.authUser.meta.phone

        this.validations = parseValidationSetup(VALIDATIONS, this)
        this.setupForm(VALIDATIONS, { first_name: '', last_name: '', email: '', phone: '' })
    },

    mounted() {
        this.mountStripe()
    },

    methods: {
        mountStripe() {
            this.stripe = Stripe(STRIPE_PUBLISHABLE_KEY)

            this.elements = this.stripe.elements({
                fonts: [{ cssSrc: 'https://fonts.googleapis.com/css2?family=Montserrat' }]
            })

            const style = {
                base: {
                    color: '#495057',
                    fontFamily: "'Montserrat', sans-serif",
                    fontSize: '16px',
                    lineHeight: '30px',
                    '::placeholder': {
                        color: '#E2E4E4',
                    },
                },
            }

            this.elements.create('cardNumber', { style, placeholder: 'XXXX XXXX XXXX XXXX' })
                .mount("#stripe-card-number")

            this.elements.create('cardExpiry', { style })
                .mount('#stripe-expiration-date')

            this.elements.create('cardCvc', { style })
                .mount('#stripe-cvv')
        },

        async submit() {
            this.$v.$touch()

            try {
                this.paying = true
                this.paymentError = null

                const paymentMethodResult = await this.stripe.createPaymentMethod({
                    type: 'card',
                    card: this.elements.getElement('cardNumber'),
                    billing_details: {
                        name: `${this.fields.first_name} ${this.fields.last_name}`,
                    },
                })

                if (paymentMethodResult.error) {
                    this.paymentError = paymentMethodResult.error.message
                    return
                }

                if (this.$v.$invalid) {
                    return
                }

                const paymentMethod = paymentMethodResult.paymentMethod

                this.fields.card = '000000000000' + paymentMethod.card.last4
                this.fields.cvv = '000'
                this.fields.expMonth = paymentMethod.card.exp_month
                this.fields.expYear = paymentMethod.card.exp_year

                const payload = Object.assign({}, this.booking.payload)

                payload.payment = {
                    card: {
                        extra: {
                            payment_method_id: paymentMethod.id,
                        },
                        number: this.fields.card.replace(/ +/g, '').trim(),
                        year: this.fields.expYear,
                        month: this.fields.expMonth,
                        code: this.fields.cvv,
                        first_name: this.fields.first_name,
                        last_name: this.fields.last_name,
                        brand: paymentMethod.card.brand,
                    },
                }
                payload.payment.billing = {
                    first_name: this.fields.first_name,
                    last_name: this.fields.last_name,
                }

                payload.items[0].options.contact = {
                    first_name: this.fields.first_name,
                    last_name: this.fields.last_name,
                    email: this.fields.email,
                    phone: this.fields.phone,
                }

                const { data: { data: order } } = await axios.post('/api/customer/order', payload)

                this.$emit('paid', order)
            } catch (e) {
                this.paymentError = getFirstErrorMessage(e)
            } finally {
                this.paying = false
            }
        },
    }
}
</script>
